// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * Step02Partner - Wizard stap 1: Partner
 * V34.9: gestructureerd partneradres consistent met Persoonlijke Gegevens.
 * Bij livesAtSameAddress='yes': prefill vanuit wizard_personal (readonly).
 * Bij livesAtSameAddress='no' : handmatig invullen.
 */
import React from 'react'
import { getProfileAddress } from '@/services/profile'
import { useEffect, useCallback, useState } from 'react'
import { Card }    from '@/components/ui/Card'
import { AddressFields } from '@/components/ui/AddressFields'
import { Input }   from '@/components/ui/Input'
import { Select }  from '@/components/ui/Select'
import { useAutosave } from '@/hooks/useAutosave'
import { useAuthStore } from '@/store'
import {loadPartner, savePartner, EMPTY_PARTNER, type PartnerData} from '@/services/wizard'
import type { SaveStatus } from '@/hooks/useAutosave'

const YES_NO = [{ value: 'yes', label: 'Ja' }, { value: 'no', label: 'Nee' }]

const RELATION_FORMS = [
  { value: 'gehuwd',        label: 'Gehuwd' },
  { value: 'geregistreerd', label: 'Geregistreerd partnerschap' },
  { value: 'samenwonend',   label: 'Samenwonend' },
  { value: 'lat',           label: 'LAT-relatie (apart wonend)' },
  { value: 'ex',            label: 'Ex-partner (relevant voor nalatenschap)' },
  { value: 'anders',        label: 'Anders' },
]

// Velden uit wizard_personal die we kunnen overnemen als zelfde adres
interface PersonalAddressFields {
  street?: string; houseNumber?: string; houseNumberAddition?: string
  postalCode?: string; city?: string
}

export function Step02Partner({ onSaveStatus, saveRef }: { onSaveStatus: (s: SaveStatus) => void; saveRef?: React.MutableRefObject<(() => Promise<void>) | null> }) {
  const { userId }  = useAuthStore()
  const [data,  setData]  = useState<PartnerData>(EMPTY_PARTNER)
  const [ready, setReady] = useState(false)

  // Adresdata van de profieleigenaar - voor prefill bij 'zelfde adres'
  const [ownAddress, setOwnAddress] = useState<PersonalAddressFields>({})

  useEffect(() => {
    if (!userId) return

    async function load() {
      // Stap 1: laad eigen adres via getProfileAddress (leest encryptedProfile + wizard_personal)
      const profileAddr = await getProfileAddress(userId!)
      const addr: PersonalAddressFields = profileAddr ?? {}
      setOwnAddress(addr)

      // Stap 2: laad partnerdata
      const d = await loadPartner(userId!)

      if (d && d.hasPartner) {
        // Bestaande data: als livesAtSameAddress='yes' → altijd huidig persoonlijk adres tonen
        // (zelfde fix als koopwoning V35-P2c: stale opgeslagen waarden nooit tonen)
        if (d.livesAtSameAddress === 'yes' && (addr.street || addr.postalCode)) {
          setData({
            ...d,
            partnerStreet:              addr.street              ?? '',
            partnerHouseNumber:         addr.houseNumber         ?? '',
            partnerHouseNumberAddition: addr.houseNumberAddition ?? '',
            partnerPostalCode:          addr.postalCode          ?? '',
            partnerCity:                addr.city                ?? '',
          })
        } else {
          setData(d)
        }
      } else if (!d) {
        // Eerste bezoek: prefill vanuit onboarding burgerlijke staat indien beschikbaar
        try {
          const stored = localStorage.getItem('wilsapp_onboarding_marital')
          if (stored) {
            const hint = JSON.parse(stored) as { hasPartner: boolean; relationForm: string; maritalStatus?: string }
            if (hint.hasPartner) {
              const isMarried = hint.maritalStatus === 'married' || hint.maritalStatus === 'registered'
              const hasAddr   = !!(addr.street || addr.postalCode)
              setData(p => ({
                ...p,
                hasPartner:   'yes',
                relationForm: hint.relationForm ?? '',
                // V39-Final: bij getrouwd + bekend adres direct zelfde adres voorstellen
                ...(isMarried && hasAddr ? {
                  livesAtSameAddress:         'yes',
                  partnerStreet:              addr.street              ?? '',
                  partnerHouseNumber:         addr.houseNumber         ?? '',
                  partnerHouseNumberAddition: addr.houseNumberAddition ?? '',
                  partnerPostalCode:          addr.postalCode          ?? '',
                  partnerCity:                addr.city                ?? '',
                } : {}),
              }))
            }
          }
        } catch { /* malformed JSON - negeer */ }
      }
      setReady(true)
    }

    load()
  }, [userId])

  const save = useCallback(async (d: PartnerData) => {
    if (userId) await savePartner(userId, d)
  }, [userId])
  const { status, saveNow } = useAutosave({ data, onSave: save, enabled: ready })
  useEffect(() => {
    if (saveRef) saveRef.current = saveNow
    return () => { if (saveRef) saveRef.current = null }
  }, [saveRef, saveNow])
  useEffect(() => { onSaveStatus(status) }, [status, onSaveStatus])

  function upd<K extends keyof PartnerData>(k: K, v: PartnerData[K]) {
    setData(p => ({ ...p, [k]: v }))
  }

  // Keuze "zelfde adres": vul adresvelden automatisch in vanuit wizard_personal
  function setLivesAtSameAddress(v: string) {
    if (v === 'yes') {
      setData(p => ({
        ...p,
        livesAtSameAddress:          'yes',
        partnerStreet:               ownAddress.street              ?? '',
        partnerHouseNumber:          ownAddress.houseNumber         ?? '',
        partnerHouseNumberAddition:  ownAddress.houseNumberAddition ?? '',
        partnerPostalCode:           ownAddress.postalCode          ?? '',
        partnerCity:                 ownAddress.city                ?? '',
      }))
    } else {
      setData(p => ({ ...p, livesAtSameAddress: v }))
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-xl font-semibold text-ink mb-1">Partner</h2>
        <p className="text-sm text-ink-3">Zodat uw nabestaanden weten wie uw naaste is en hoe zij bereikt kunnen worden.</p>
      </div>

      {/* Ja/Nee - tonen alleen als burgerlijke staat dit niet al impliceert */}
      {(() => {
        // Lees hint uit onboarding - als gebruiker al aangaf getrouwd/geregistreerd te zijn,
        // sla de vraag over en toon direct de partnergegevens
        let maritalImpliesPartner = false
        let maritalLabel = ''
        try {
          const stored = localStorage.getItem('wilsapp_onboarding_marital')
          if (stored) {
            const hint = JSON.parse(stored) as { hasPartner?: boolean; maritalStatus?: string }
            if (hint.hasPartner) {
              maritalImpliesPartner = true
              maritalLabel = hint.maritalStatus === 'married'    ? 'Getrouwd'
                           : hint.maritalStatus === 'registered' ? 'Geregistreerd partnerschap'
                           : ''
            }
          }
        } catch { /* negeer */ }

        if (maritalImpliesPartner) {
          // Geen vraag - alleen een bevestigingsregel met optie om te wijzigen
          return (
            <div className="flex items-center justify-between rounded-xl bg-sage-50 border border-sage-100 px-4 py-3">
              <div>
                <p className="text-xs font-semibold text-sage-700">
                  {maritalLabel || 'Partner aanwezig'} - u gaf dit aan bij burgerlijke staat
                </p>
                <p className="text-xs text-sage-600 mt-0.5">Vul hieronder de gegevens van uw partner in.</p>
              </div>
              <button type="button"
                onClick={() => { upd('hasPartner', 'no') }}
                className="text-xs text-ink-4 underline underline-offset-2 hover:text-ink-3 flex-shrink-0 ml-4">
                Toch geen partner
              </button>
            </div>
          )
        }

        return (
          <div className="bg-white rounded-2xl border border-sand-300 p-5">
            <p className="text-base font-medium text-ink mb-4">Heeft u een partner?</p>
            <div className="flex flex-col gap-2">
              {YES_NO.map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => upd('hasPartner', opt.value)}
                  className={`flex items-center gap-3 rounded-xl border-2 px-4 py-4 text-left transition-all ${
                    data.hasPartner === opt.value ? 'border-sage-600 bg-sage-50' : 'border-sand-300 bg-white hover:border-sage-300'
                  }`}>
                  <div className={`h-5 w-5 rounded-full border-2 flex-shrink-0 ${
                    data.hasPartner === opt.value ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
                  }`} />
                  <span className="text-base font-semibold text-ink-2">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
        )
      })()}

      {data.hasPartner === 'yes' && (
        <div className="flex flex-col gap-4 animate-slide-down">

          {/* Persoonsgegevens */}
          <Card>
            <h3 className="text-sm font-semibold text-ink mb-3">Persoonsgegevens</h3>
            <div className="flex flex-col gap-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Voornaam partner" value={data.name}
                  onChange={e => upd('name', e.target.value)} placeholder="Voornaam" autoFocus />
                <Input label="Achternaam" value={data.lastName}
                  onChange={e => upd('lastName', e.target.value)} placeholder="Achternaam" />
              </div>
              <Select label="Relatievorm" options={RELATION_FORMS} value={data.relationForm}
                onChange={e => upd('relationForm', e.target.value)} placeholder="Kies relatievorm..." />
              <Input label="Geboortedatum (optioneel)" type="date" value={data.dob}
                onChange={e => upd('dob', e.target.value)} />
            </div>
          </Card>

          {/* Contactgegevens */}
          <Card>
            <h3 className="text-sm font-semibold text-ink mb-3">Contactgegevens</h3>
            <p className="text-xs text-ink-4 mb-3 leading-relaxed">
              Voor uw nabestaanden - zodat zij uw partner direct kunnen bereiken.
            </p>
            <div className="flex flex-col gap-3">
              <Input label="Telefoonnummer partner" type="tel" value={data.phone ?? ''}
                onChange={e => upd('phone', e.target.value)} placeholder="06 12 34 56 78" />
              <Input label="E-mailadres partner (optioneel)" type="email" value={data.email ?? ''}
                onChange={e => upd('email', e.target.value)} placeholder="naam@voorbeeld.nl" />
            </div>
          </Card>

          {/* Woonsituatie */}
          <Card>
            <h3 className="text-sm font-semibold text-ink mb-3">Woonsituatie</h3>
            <div className="flex flex-col gap-4">

              {/* Ja / Nee knoppen */}
              <div>
                <p className="text-sm font-medium text-ink-2 mb-2">
                  Woont uw partner op hetzelfde adres?
                </p>
                <div className="flex gap-2">
                  {YES_NO.map(opt => (
                    <button key={opt.value} type="button"
                      onClick={() => setLivesAtSameAddress(opt.value)}
                      className={`flex-1 rounded-xl border-2 py-2.5 text-sm font-medium transition-all ${
                        data.livesAtSameAddress === opt.value
                          ? 'border-sage-600 bg-sage-50 text-sage-800'
                          : 'border-sand-300 text-ink-3 hover:border-sage-300'
                      }`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Zelfde adres - Optie B: compacte adreskaart (V41-P1 fix) */}
              {data.livesAtSameAddress === 'yes' && (
                <div className="animate-slide-down rounded-2xl bg-sage-50 border border-sage-200 px-4 py-3 flex flex-col gap-1.5">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-sage-700">✓ Adres overgenomen</span>
                  </div>
                  {data.partnerStreet ? (
                    <>
                      <p className="text-sm text-ink font-medium">
                        {data.partnerStreet} {data.partnerHouseNumber}{data.partnerHouseNumberAddition ? ' ' + data.partnerHouseNumberAddition : ''}
                      </p>
                      <p className="text-sm text-ink-3">
                        {data.partnerPostalCode} {data.partnerCity}
                      </p>
                    </>
                  ) : (
                    <p className="text-xs text-ink-4 italic">
                      Vul eerst uw adres in bij Persoonlijke gegevens.
                    </p>
                  )}
                  <p className="text-xs text-sage-600 mt-1">
                    Adres wijzigen? Pas het aan via{' '}
                    <span className="font-semibold">Persoonlijke gegevens</span>.
                  </p>
                </div>
              )}

              {/* Afwijkend adres - V40: autofill op postcode + huisnummer */}
              {data.livesAtSameAddress === 'no' && (
                <div className="animate-slide-down">
                  <AddressFields
                    values={{
                      postalCode:          data.partnerPostalCode,
                      houseNumber:         data.partnerHouseNumber,
                      houseNumberAddition: data.partnerHouseNumberAddition,
                      street:              data.partnerStreet,
                      city:                data.partnerCity,
                    }}
                    onChange={(field, value) => {
                      const map: Record<string, string> = {
                        postalCode:          'partnerPostalCode',
                        houseNumber:         'partnerHouseNumber',
                        houseNumberAddition: 'partnerHouseNumberAddition',
                        street:              'partnerStreet',
                        city:                'partnerCity',
                      }
                      upd(map[field] as keyof typeof data, value)
                    }}
                    showAddition
                    labels={{ street: 'Straatnaam partner', city: 'Woonplaats partner' }}
                  />
                </div>
              )}
            </div>
          </Card>

          {/* Aanvullende notities */}
          <Card>
            <h3 className="text-sm font-semibold text-ink mb-3">Aanvullende notities (optioneel)</h3>
            <textarea
              value={data.notes}
              onChange={e => upd('notes', e.target.value)}
              placeholder="Bijv: trouwdatum, bijzonderheden voor naasten..."
              className="w-full rounded-xl border border-sand-300 bg-white px-4 py-3 text-sm text-ink resize-none focus:outline-none focus:ring-2 focus:ring-sage-400 focus:border-transparent min-h-[80px]"
              maxLength={400}
            />
          </Card>

          <div className="rounded-xl bg-sand px-4 py-3 text-xs text-ink-4 leading-relaxed">
            <strong className="text-ink-3">Tip:</strong> Noteer ook uw trouwdatum of datum van
            partnergegevens - dit kan relevant zijn voor de nalatenschap.
          </div>
        </div>
      )}
    </div>
  )
}
